import React, { useMemo } from 'react';
import isUrl from 'validator/es/lib/isURL';
import { SmartLink, splitAndRetainDelimiters } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { CheckboxField, useFormField } from '@frontend/design-system';
import { MIN_MODAL_WIDTH } from '../../../constants';
import { useUserMentions, useMessageContent } from '../../../hooks/message-parsing.hooks';
import { useTeamChatSelector } from '../../../providers/team-chat.provider';
import { MentionRenderer } from './mention-renderer';
import { messageBodyTextStyles } from './message-body-text.styles';

export const MessageBodyText = ({ message }: { message: string }) => {
  const { t } = useTranslation('team-chat');
  const { users, currentUser } = useUserMentions();
  const { allDelimiters } = useMessageContent(message);
  const noShowFormFieldProps = useFormField({ type: 'checkbox', value: false });
  const { setNoShowLinksSecurityModal } = useTeamChatSelector((ctx) => ctx.cache);
  const hideLinksSecurityModal = useTeamChatSelector((ctx) => ctx.hideLinksSecurityModal);

  const nodes = useMemo(() => {
    const textSegments = splitAndRetainDelimiters(message, allDelimiters);

    return textSegments.map((segment, index) => {
      const isMention = segment.startsWith('@');
      const isLink = isUrl(segment);

      if (isLink) {
        const onClickConfirm = () => {
          if (noShowFormFieldProps.value) {
            setNoShowLinksSecurityModal();
          }
        };

        return (
          <SmartLink
            key={`link-${index}`}
            text={segment}
            index={index}
            urlProps={{
              ...(!hideLinksSecurityModal && {
                confirmationModalProps: {
                  title: t(`You’re leaving Weave App`),
                  message: t(
                    `This link will take you to an external website. If you trust this link, tap on Open Link to continue.`
                  ),
                  confirmLabel: t('Open Link'),
                  confirmTrackingId: 'team-chat-2.0-links-security-conversation-modal-confirmation-button',
                  cancelTrackingId: 'team-chat-2.0-links-security-conversation-modal-cancel-button',
                  children: (
                    <CheckboxField
                      name={t('No Show checkbox')}
                      label={t(`Don’t show this message again`)}
                      labelColor='subdued'
                      trackingId='team-chat-2.0-skip-links-security-confirmation-modal-button'
                      {...noShowFormFieldProps}
                    />
                  ),
                  onConfirm: onClickConfirm,
                  maxWidth: MIN_MODAL_WIDTH,
                },
              }),
            }}
          />
        );
      }

      if (isMention) {
        return (
          <MentionRenderer
            key={`mention-${index}`}
            mentionText={segment}
            users={users}
            currentUser={currentUser}
            index={index}
          />
        );
      }

      return <React.Fragment key={`text-${index}`}>{segment}</React.Fragment>;
    });
  }, [
    message,
    allDelimiters,
    users,
    currentUser,
    t,
    noShowFormFieldProps,
    hideLinksSecurityModal,
    setNoShowLinksSecurityModal,
  ]);

  return <span css={messageBodyTextStyles.container}>{nodes}</span>;
};
